/**
 * The redeem screen — where a personal sign-in link lands (spec 09 §3.3).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE LINK, AND WHY IT LOOKS LIKE THIS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The Technical Delegate copies a link of this exact shape out of Tournament Configuration
 * and sends it himself (D19/D20):
 *
 *     https://<the tournament host>/sign-in#token=<the token>
 *
 * **The token is in the fragment, and the fragment is the point.** A fragment is never put
 * in a request line, so it reaches no access log, no proxy log and no referrer header
 * (spec 09 §2, SECURITY T3). It is read here and struck from the address bar immediately, held
 * in memory, and POSTed once to `/auth/redeem` only when the person presses `Continue To Sign
 * In` - never because it arrived (RUN 87, below). The strike waits for neither the press nor
 * the exchange, and holds whether the exchange succeeds, fails or never happens, because a dead
 * token left in a URL is still a token somebody may forward.
 *
 * A bare fragment (`#<token>`, no `token=`) is accepted as well. Nothing in the system
 * generates that form; it is honoured because a mail client that mangles a query-shaped
 * fragment is a cheaper thing to tolerate than to debug at a venue. A fragment that is
 * mangled *beyond reading* — a stray `%`, which cannot be percent-decoded — is refused on
 * screen with `This Link Could Not Be Read`, never thrown.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS SCREEN NEVER DOES
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  · It never redeems a link because the link arrived (RUN 87). Opening the link reads it,
 *    strikes it and holds it; only a press of `Continue To Sign In` sends it.
 *  · It never writes the token down. Until the press it is held in memory, inside the one
 *    function that will send it: never in `localStorage`, `sessionStorage` or IndexedDB, never
 *    as a string in React state, never logged.
 *  · It never renders the credential, and never puts it in React state. `storeCredential`
 *    takes it straight from the response into storage; nothing else here touches it.
 *  · It never logs the token or the credential — not to `console`, not in an error message.
 *  · It never redeems a link twice at once. The token is single-use (gate G-16), so a second
 *    POST while the first is out would spend a live link and land the person on `This Link Has
 *    Already Been Used` after a successful sign-in. Every exchange that is out is held by its
 *    token, and a link that arrives again while its exchange is out — on this screen or on one
 *    mounted after it — joins that exchange instead of sending another. Since RUN 87 a press
 *    does the same: `Continue To Sign In` pressed twice, or pressed for a token whose exchange
 *    is out, joins it. A link whose exchange has ANSWERED may be opened again — that is a new
 *    press, and the server, not this screen, decides whether a used link still works.
 *  · It offers no `Send Me A New Link` control. This application sends no email (D19/D20).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ARRIVING IS NOT PRESSING (RUN 87, 19 SEPTEMBER 2026 - THE TECHNICAL DELEGATE'S RULING)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Umpires Managers kept finding their personal links refused. The links go out over WhatsApp,
 * and anything that rendered this page - WhatsApp's own browser, or a first tap on the wrong
 * phone or laptop - spent the single-use token on arrival, because the screen POSTed it the
 * moment it was read. The manager then opened the same link in their real browser and it was
 * already spent; and a fresh link revokes the one before it (one live link per person), so the
 * next open-here-then-there spent that one too.
 *
 * So arriving no longer redeems. A link is read, struck and numbered exactly as before, and the
 * screen then stands with ONE button, `Continue To Sign In`; the token is POSTed only when that
 * is pressed. A link opened and not pressed has not been used, so opening it again from the
 * message - on the right device, in the right browser - still works. What that means below:
 *
 *  · The token waits in memory only, inside the press that will send it (`Redemption`, `ready`).
 *    It goes with the screen: a reload finds no link, and the screen says to open it again.
 *  · A PRESS is numbered with the arrivals, and is the newest thing on the page when it happens,
 *    so of two presses - or of a press and a link that arrives after it - only the newest acts.
 *  · One POST per token still holds, now at the press: a press of a token whose exchange is out
 *    joins that exchange (a double press, or two presses that reach the screen before it redraws).
 *  · A link that arrives while ITS OWN exchange is out joins it at once, as it always did, and the
 *    screen says `Signing You In`: it has been pressed on this page already. Holding it for a
 *    second press would let that exchange's answer - perhaps a success, the link then spent -
 *    reach nobody, which is security QA pass 3's finding in another form.
 *  · A DIFFERENT link that arrives while an exchange is out is held for its own press, and the
 *    exchange that is out loses the right to act, exactly as a pasted link always has.
 *  · A browser built into WhatsApp, Instagram, Facebook, LINE or WeChat gets a hint above the
 *    button to open the link in Safari or Chrome instead (`app/inAppBrowser.ts`). Advisory only:
 *    the button stands in every browser.
 *
 * No server change. Only `POST /auth/redeem` spends a link, and only a successful one - the
 * claim and the pairing commit together or not at all - while `GET /auth/redeem` refuses without
 * reading anything. The fragment never reaches the server in the first place.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A PRESS IS KEPT (RUN 87A, 19 SEPTEMBER 2026 - THE SECURITY REVIEW OF RUN 87)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The review drove RUN 87 in Chrome, passed it, and found four things, fixed here:
 *
 *  - L1a - the lone second half of a double-click pressed the button: the DoubleClickjacking
 *    shape, where another page gets a double-click started and puts this button under its second
 *    click. A click whose `detail` is above 1 is ignored. A keyboard press reports 0 and a tap or a
 *    single click reports 1, so nobody who means to press is refused.
 *  - L1b - a device that was already signed in was shown the button with no word of it. It is now
 *    told, above the button, who it is signed in as - the name the session already has, since the
 *    boot check fetched it; nothing is asked of the network for it - or, while no name is known,
 *    simply that it is signed in; and that continuing replaces that sign-in with this link's.
 *  - L3 - a PRESSED link could be spent without signing anybody in. Press A; a different link B
 *    arrives while A is out; A succeeds on the server - and A's credential was dropped, because B
 *    was the latest arrival. The server had paired a device for A's owner all the same, and that
 *    pairing can evict one of their real devices (two bindings each). A press is a deliberate act:
 *      - A pressed link's SUCCESS IS KEPT. Its credential is stored and the device is signed in with
 *        it, whatever has arrived since. It moves the screen on only if nothing newer is on it and no
 *        newer link waits in the address bar; otherwise the newer link stays exactly where it is,
 *        held for its own press, and says (L1b) whose sign-in continuing would replace. A REFUSAL
 *        still changes nothing unless it is the newest thing on the page: a refused press spent
 *        nothing.
 *      - ONE PRESSED LINK AT A TIME. While a link's exchange is out, a different link's button is
 *        drawn but cannot be pressed until that exchange has answered. Otherwise a second press could
 *        sign in first, and the first press's success could then only be dropped - #17 forbids it
 *        re-signing the device in over a newer sign-in - leaving it spent, unkept and untold.
 *      - A refusal on screen no longer claims `Nothing has been signed in on this device` when the
 *        device is signed in; it says who it is signed in as.
 *  - The button's footer says a new link can be asked for, and that it cancels this one.
 *
 * What that does to the rules below. `An older answer changes nothing` now holds for REFUSALS. An
 * older SUCCESS - only a pressed link has one - is kept, but it can no longer land on top of a newer
 * sign-in, because nothing else can be pressed until it has answered, and it never takes the screen
 * from a newer link, read or unread. So the failure #17 and the second review guard against - a
 * device silently re-signed in as an older link's owner after the person's own link signed them in -
 * still cannot happen: the older link answers first, the screen then says whose sign-in it is, and
 * the person's own link, pressed after that, replaces it.
 *
 * RUN 87B closed the two points left open after that:
 *
 *  - AN ANSWER THAT NEVER ARRIVES IS NOT A REFUSAL. The screen used to say the link `has not been
 *    spent` when the connection dropped, which it cannot know: the server claims the link and pairs
 *    the device before it replies, so a reply lost on the way back leaves the link spent and this
 *    device without its credential. Now a press whose answer never arrived intact - the connection
 *    failed, it dropped part-way through the reply, or a gateway answered instead of the API - brings
 *    the button back for the same link, still held in memory, saying the sign-in could not be
 *    confirmed and what an `already used` on the next press means.
 *  - A REFUSAL ON A DEVICE THAT IS SIGNED IN HAS A WAY ON. It was either signed in before this link,
 *    or a link pressed before this one signed it in since (a pressed success is kept), and it used to
 *    sit on the refusal with nowhere to go. It now offers `Continue As <name>`, into the app, to the
 *    same place a sign-in made here would have gone.
 *
 * RUN 87C closed three more from the security re-review of that round:
 *
 *  - N1 - A PRESS THAT HANGS NO LONGER HOLDS THE PAGE. The redeem POST had no time limit, and with
 *    one pressed link at a time a hung press blocked every link opened after it, for good. It now
 *    gives up after `REDEEM_REQUEST_TIMEOUT_MS` (in `api/client.ts`) as a dropped connection does,
 *    so it lands on `Sign-In Not Confirmed` with its button back, and a link held behind it is
 *    released. The line under a held link's inert button also says what to do if it does not.
 *  - N2 - ANY 5xx IS UNCONFIRMED, NOT REFUSED: the redeem route can fail after claiming the link.
 *    The API's own 4xx refusals stay final. `This Link Has Already Been Used` now also says that a
 *    press on this device that did not finish may be what used it.
 *  - N3 - A 200 THE SCREEN CANNOT USE IS UNCONFIRMED TOO, where it used to leave `Signing You In`
 *    up for good: `redeemSignInLink` checks the shape of the answer before anything reads it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A LINK OPENED IN A TAB THAT IS ALREADY HERE (DECISION #17 = A, 17 SEPTEMBER 2026)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A personal link pasted into a tab that is already showing this screen changes nothing but
 * the fragment, and the browser answers that with no reload and React with no remount. The
 * fragment used to be read once, when the screen mounted, so that link was ignored: the screen
 * went on saying `This Device Is Not Signed In` with the token sitting in the address bar. Every
 * ARRIVAL of a link is now read — the one the page opened with, and any that comes after it.
 *
 * Corrected 18 September 2026, after a security review drove the real application in Chrome.
 * The first version of this passed its tests and did nothing in a browser:
 *
 *  · **An arrival is a change of the location's `hash`, not of its `key`.** `BrowserRouter`
 *    reads the key out of `history.state`, and every entry the BROWSER makes — a cold load, a
 *    fragment navigation — carries none, so all of them read `"default"` and an effect keyed on
 *    the key never ran for a pasted link. Only `MemoryRouter`, which the tests used, mints a key
 *    per navigation. The tests of this now drive `BrowserRouter` and paste as a browser does.
 *  · **The fragment is struck THROUGH THE ROUTER**, as a replace to the same path and query.
 *    Struck behind the router's back, the router went on holding a fragment the address bar no
 *    longer had, and the same link opened again was no change it could see.
 *  · **Only the latest arrival acts.** Exchanges overlap — a slow link, then a fast one — and
 *    each arrival is numbered: only the newest may store a credential, sign in, navigate or put
 *    a refusal on screen. An older answer changes nothing, success or refusal. Without that, a
 *    TD who pasted a forwarded link by mistake and then his own was signed in as himself, and
 *    then, seconds later, silently re-signed in as whoever the first link belonged to.
 *    (RUN 87A: still so for a refusal. A PRESSED link's success is now kept - stored and signed
 *    in - though it moves the screen only if nothing newer is on it; see A PRESS IS KEPT above.)
 *
 * Corrected again the same day, after a second review (security QA, pass 2) drove it through the
 * browser's history. Both rules above held only while ONE sign-in screen stayed mounted, and a tab
 * mounts more than one: Back to a gated page and Forward again takes this screen down and puts a
 * new one up while the old one's exchange is still out — and `useNavigate` from a screen that has
 * gone still navigates. So:
 *
 *  · **The arrival count and the exchanges out belong to the page, not to one screen** — module
 *    scope, below. Held by the screen, a UM's slow link pasted on the old screen answered after
 *    the TD's own link had signed him in on the new one, and re-signed the device in as her; and
 *    the same single-use link opened again on the new screen was POSTed a second time.
 *  · **An answer that lands while a link is waiting in the address bar changes nothing.** The
 *    router renders a pasted link in a transition, so an exchange can answer after the address bar
 *    holds a newer link and before any screen has read it — when it is still "the latest arrival"
 *    by number, and about to navigate the newer link away unread. The newer link decides, once read.
 *    (RUN 87A: a pressed success that lands then is kept - stored and signed in - but still does
 *    not navigate; the newer link is read, held for its press, and says whose sign-in it replaces.)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE A REDEEMED LINK LANDS (GAP J9)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **On the landing screen of the kind of user who signed in**, unless somebody was on their
 * way somewhere else. A Technical Delegate lands on the Command Desk; an Umpires Manager
 * lands on `/um`, the app that is actually hers. She used to land on the Control Centre
 * shell — a masthead of modules whose every read her credential is refused for — and had to
 * be told to type `/um` by hand. This is a routing default and nothing more: no gate moved,
 * and a UM who reaches a Control Centre path is refused by the API exactly as before.
 *
 * The session gate sends
 * a stopped deep link here as `?next=<the path>`, this screen remembers it across the
 * navigation that brings the personal link in — a different URL, with no `next` on it — and
 * hands it back once the credential is stored. What may be resumed is decided by
 * `app/resume.ts` against an allowlist of this application's own routes: a crafted `next` is
 * discarded in silence and the person lands on the Command Desk, which is what they would
 * have done before any of this existed.
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { SIGN_IN_NEXT_PARAM, SIGN_IN_PATH } from "@hockeytd/shared";

import { ApiError, redeemSignInLink, storeCredential } from "../api/client.js";
import { deviceLabel } from "../app/deviceLabel.js";
import { inAppBrowserName } from "../app/inAppBrowser.js";
import { rememberResumePath, resumeTarget } from "../app/resume.js";
import {
  useSession,
  type Session,
  type SessionValue,
  type SignedOutReason,
} from "../app/session.js";
import { Skeleton } from "../app/states.js";
import { SignedOutPage } from "./SignedOutPage.js";

/**
 * The path the sign-in link points at, re-exported from the shared package so the route
 * table, the link the API prints and the link Tournament Configuration displays are one
 * string (stance D2, `packages/shared/src/sign-in-link.ts`).
 */
export { SIGN_IN_PATH };

/**
 * What the session gate hands over when it sends a device here: why it stopped them. It is
 * carried in the history entry's state rather than the URL — see `App.tsx` — so it is absent
 * on a reload or on a link opened cold, and `no_credential` is the honest reading of both.
 */
export interface SignInLandingState {
  reason: SignedOutReason;
}

/**
 * Spec 09 §3.3's three named failures, keyed by the API's own wire codes
 * (`apps/api/src/routes/auth.ts`). An unknown token arrives as `link_expired`, on purpose:
 * a token that never existed and one that lapsed are the same situation for the person
 * holding it, and telling them apart would let somebody probe for live links.
 *
 * The `Send Yourself A New One` half of the specified copy is rewritten to name the
 * Technical Delegate, because no self-service re-send exists to point at (D19/D20).
 *
 * The expired copy states NO lifetime. The specified `Links last 24 hours` stopped being true
 * on 2 September 2026, when the lifetime became a year so testers can open a link whenever they
 * get to it, and the year became thirty days on 19 September 2026 (Decision #10 = B;
 * `MAGIC_LINK_TTL_HOURS`, `apps/api/src/repositories/auth.ts`). A number printed here is a
 * second copy of a server constant, and this one has now changed twice.
 */
interface Refusal {
  title: string;
  detail: string;
}

const REFUSAL: Readonly<Record<string, Refusal>> = {
  link_expired: {
    title: "This Link Has Expired",
    detail:
      "This link is too old to use. Ask the Technical Delegate for a fresh one — it takes him " +
      "a second to issue.",
  },
  link_already_used: {
    title: "This Link Has Already Been Used",
    // RUN 87C (N2) - the last sentence: a press on this device whose answer never came back can be
    // the press that used the link, and the next press is then refused as used.
    detail:
      "Links work once. If you are setting up a second device, ask the Technical Delegate " +
      "for a fresh one. If you pressed Continue To Sign In on this device and it did not " +
      "finish, that press may have used it: reload this page to see whether this device is " +
      "signed in, or ask the Technical Delegate for a new link.",
  },
  link_revoked: {
    title: "This Link Is No Longer Valid",
    detail: "It has been replaced or withdrawn. Ask the Technical Delegate for a new one.",
  },
};

/**
 * The fourth refusal, and the only one the server never sends: the link never became a token
 * at all. A mail client that rewrote the link can leave a stray `%` in the fragment, and
 * percent-decoding one throws — so the honest screen is a refusal that says the link arrived
 * damaged, not a blank page and not `This Device Is Not Signed In`, which would describe a
 * link that was never opened.
 */
const LINK_UNREADABLE: Refusal = {
  title: "This Link Could Not Be Read",
  detail:
    "The link arrived damaged — part of it was lost or rewritten on the way here. Ask the " +
    "Technical Delegate for a fresh one; this one was never used.",
};

/**
 * Any other refusal the API itself gave - a 4xx with its own error body and a code outside the
 * three above. A venue wifi that dropped is not here since RUN 87B, nor a 5xx since RUN 87C: see
 * {@link signInNotConfirmed}.
 */
function unexpectedRefusal(): Refusal {
  return {
    title: "This Link Could Not Be Used",
    detail:
      "Something refused the exchange. Open the link again, and if it still fails ask " +
      "the Technical Delegate for a fresh one.",
  };
}

/**
 * Whether a press's exchange ended without telling this device whether the link was spent - so
 * the sign-in can only be said to be unconfirmed, never refused:
 *
 *  - RUN 87B: the answer never arrived intact. The connection failed, it dropped part-way through
 *    the reply, or something in front of the API answered instead of it (a gateway error with no
 *    API body on it).
 *  - RUN 87C (N1): the press ran out of time - `redeemSignInLink` gives up after
 *    `REDEEM_REQUEST_TIMEOUT_MS` as a dropped connection does.
 *  - RUN 87C (N2): ANY 5xx, API body or not. The server failed while handling the press, and it
 *    can fail after the claim has committed: the redeem route reads the tournament for its answer
 *    after claiming the link, so a 500 there leaves the link spent and this device without its
 *    credential.
 *  - RUN 87C (N3): a 200 that was not an answer the screen can use (refused by `redeemSignInLink`
 *    as `unexpected_error`). A 200 means the claim committed.
 *
 * The screen used to say the link `has not been spent`, which it cannot know. It now says the
 * sign-in could not be confirmed, and stands the button again for the same link, still held in
 * memory. The API's own refusals - a 4xx with its error body, above all the three codes - are final
 * and are not here: they say what happened to the link.
 */
function signInNotConfirmed(error: unknown): boolean {
  return (
    !(error instanceof ApiError) ||
    error.code === "network_error" ||
    error.code === "unexpected_error" ||
    error.status >= 500
  );
}

/**
 * What a fragment turned out to be. Three outcomes, and the third is the one that matters:
 * a fragment that is *there* but cannot be read is not the same situation as no fragment at
 * all, and the two screens differ.
 */
export type SignInFragment =
  | { kind: "token"; token: string }
  | { kind: "absent" }
  | { kind: "malformed" };

/**
 * `#token=abc` or `#abc`; anything else is no token at all.
 *
 * `decodeURIComponent` throws a `URIError` on a stray `%` — `#ab%zz`, which is exactly what a
 * mail client that mangled the link leaves behind — so the decode is caught here and reported
 * as `malformed`. It used to throw out of a `useState` initialiser, with no boundary above it:
 * a white screen, at a venue, on the one screen a person cannot get past.
 */
export function readSignInFragment(hash: string): SignInFragment {
  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  if (fragment === "") return { kind: "absent" };

  const named = new URLSearchParams(fragment).get("token");
  let bare: string | null = null;
  if (named === null && !fragment.includes("=")) {
    try {
      bare = decodeURIComponent(fragment);
    } catch {
      // A percent-escape that is not one. Nothing here is logged: the fragment is the token.
      return { kind: "malformed" };
    }
  }

  const token = (named ?? bare)?.trim();
  return token === undefined || token === "" ? { kind: "absent" } : { kind: "token", token };
}

/** The token alone, for callers that do not distinguish "no link" from "unreadable link". */
export function tokenFromFragment(hash: string): string | null {
  const read = readSignInFragment(hash);
  return read.kind === "token" ? read.token : null;
}

type Redemption =
  /**
   * A readable link has arrived and waits for `Continue To Sign In` (RUN 87). `press` sends it,
   * and is the one place the token waits: in that function's closure, in memory, for as long as
   * this view stands - never as a string in state, where a devtools pane would print it. `null`
   * only between the first render and the effect that reads the arrival, when the button is
   * drawn but cannot be pressed yet.
   *
   * `waiting` (RUN 87A): a different link's exchange is out on this page, so this one cannot be
   * pressed until it has answered - one pressed link at a time (see A PRESS IS KEPT above).
   *
   * `unconfirmed` (RUN 87B): this link was pressed and its answer never arrived
   * ({@link signInNotConfirmed}), so the button stands again and says the sign-in could not be
   * confirmed.
   */
  | { status: "ready"; press: (() => void) | null; waiting: boolean; unconfirmed: boolean }
  | { status: "redeeming" }
  | { status: "refused"; refusal: Refusal };

/** One exchange of a token for a credential, as sent — shared by every arrival of that token. */
type Exchange = ReturnType<typeof redeemSignInLink>;

/**
 * The number of the latest arrival of a link, on ANY sign-in screen this page has mounted. An
 * exchange's answer is acted on only by the arrival holding this number — so of two overlapping
 * exchanges it is the link pasted LAST that signs this device in, whichever answers last and
 * whichever screen each was pasted on.
 *
 * RUN 87: a press of `Continue To Sign In` takes the next number too, because the press is now
 * what sends a link, and at the moment it happens it is the newest thing on the page. Of two
 * presses of one link (a double press) the second joins the first one's exchange and is the one
 * that acts on its answer; a link that arrives after a press takes the right to act from it,
 * exactly as a later arrival always took it from an earlier one.
 *
 * RUN 87A: "act" here means move the screen - navigate, or put a refusal on it. Keeping a pressed
 * link's success (storing its credential, signing in) does not wait on this number; see
 * {@link sendPressedLink}.
 *
 * Module scope, not a ref of the screen (security QA, pass 2, 18 September 2026): what it guards —
 * the address bar, and the credential cache in `credentialStore.ts`, itself module scope — is one
 * per page, and a mounted screen is not. Not the session provider either: that is also mounted
 * once per page, and holding this there would put one screen's bookkeeping into the value every
 * screen reads, for no difference in lifetime.
 */
let latestArrival = 0;

/**
 * The exchanges that are out, by token, whichever screen sent them. A token that arrives again
 * while its exchange is out — the same link pasted twice, or the same link opened on a screen
 * mounted since — joins that exchange instead of POSTing a single-use token a second time, and
 * so, since RUN 87, does a press of a token whose exchange is out: `Continue To Sign In` pressed
 * twice sends the link once. It is released the moment its exchange answers, whoever that answer
 * turns out to matter to, so the link can be opened again.
 */
const outstanding = new Map<string, Exchange>();

/** Test seam. Forgets every arrival and every exchange out, so no link crosses into the next test. */
export function resetSignInArrivals(): void {
  latestArrival = 0;
  outstanding.clear();
}

/**
 * Whether a DIFFERENT link's exchange is out on this page (RUN 87A). While one is, this link's
 * button cannot be pressed: one pressed link at a time, so that every pressed link answers before
 * another can be sent, and its success can always be kept (see A PRESS IS KEPT at the head of this
 * file). The link's own exchange does not count - a second press of it joins that exchange.
 */
function anotherLinkOut(token: string): boolean {
  for (const out of outstanding.keys()) {
    if (out !== token) return true;
  }
  return false;
}

/**
 * Who this device is already signed in as, as far as the session knows without asking the network
 * (L1b, RUN 87A): a name once the boot check - or a sign-in made here - has one; `null` when a
 * credential is held but its owner is not known yet (the check still out, or the API unreachable);
 * and nothing at all when the device holds no credential.
 */
function signedInAs(session: Session): { name: string | null } | null {
  switch (session.status) {
    case "signed-in":
      return { name: session.identity.appShortName };
    case "unreachable":
      return { name: null };
    case "checking":
      return session.credentialHeld === true ? { name: null } : null;
    case "signed-out":
      return null;
  }
}

/**
 * Whether the address bar holds a newer link that no screen has read yet (security QA, pass 2).
 *
 * Every arrival is struck from the address bar the moment it is read, so an answer normally finds
 * the fragment empty. It finds one when a newer link has been pasted and the router has not yet
 * rendered it — `BrowserRouter` renders a new location in a transition, and an exchange can answer
 * in between. That answer only LOOKS like the latest, because the newer link has not been counted
 * yet; acted on, it would sign in and navigate the newer link out of the address bar unread.
 */
function newerLinkWaitingInAddressBar(token: string): boolean {
  // Security QA pass 3 — only a DIFFERENT link. The same link opened again joins this exchange
  // (one POST per token), so its answer is that link's answer too; skipping it dropped a sign-in
  // that had succeeded, spent the link, and left a device binding nobody holds.
  const waiting = readSignInFragment(window.location.hash);
  return waiting.kind === "token" && waiting.token !== token;
}

/**
 * Send a pressed link: the one POST of its token, registered in {@link outstanding} so that any
 * press or arrival of the same link joins it rather than sending it again.
 *
 * Two handlers go on the exchange here, once, before any screen's own:
 *
 *  - It is RELEASED when it answers, either way and whether or not any screen still cares: the same
 *    link may be opened again, and the server will say what it says.
 *  - L3 (RUN 87A) - A PRESS IS KEPT. If it succeeds, its credential is stored and the device signed
 *    in with it, whatever has arrived since and whichever screen - if any - is still up. Only
 *    moving the screen (navigating, or drawing a refusal) is left to the screens' own handlers and
 *    their newest-wins rule. Dropping a success here is what spent a pressed link without signing
 *    anybody in: the server had already paired a device for the link's owner, and that pairing can
 *    evict one of their real devices. Nothing else can be pressed while this is out
 *    ({@link anotherLinkOut}), so no newer sign-in can have landed before it.
 *
 * `signIn` is the session provider's, which outlives any one sign-in screen.
 */
function sendPressedLink(token: string, signIn: SessionValue["signIn"]): Exchange {
  const sent = redeemSignInLink(token, deviceLabel());
  outstanding.set(token, sent);
  const release = (): void => {
    if (outstanding.get(token) === sent) outstanding.delete(token);
  };
  sent.then((body) => {
    release();
    // Straight into storage. The credential is not returned, held or rendered.
    storeCredential(body.credential);
    signIn(body.identity, body.tournament ?? null);
  }, release);
  return sent;
}

export function SignInPage(): React.JSX.Element {
  const { hash, pathname, search, state: landingState } = useLocation();
  const navigate = useNavigate();
  const { session, signIn } = useSession();
  /**
   * What the latest link to arrive turned out to be — its KIND, and never the token, which has no
   * business sitting in React state where a devtools pane can read it.
   */
  const [fragment, setFragment] = useState<SignInFragment["kind"]>(
    () => readSignInFragment(hash).kind,
  );
  // A readable link is shown its button from the first frame (RUN 87), inert until the effect
  // below has read the arrival - never `Signing You In`, which would say something had been sent.
  const [state, setState] = useState<Redemption>(() =>
    fragment === "malformed"
      ? { status: "refused", refusal: LINK_UNREADABLE }
      : { status: "ready", press: null, waiting: false, unconfirmed: false },
  );

  // The destination the session gate was carrying, put somewhere that survives the next
  // navigation — the personal link is a different URL and arrives with no `next` on it. A
  // `next` that is not one of this application's own routes clears the store rather than
  // being kept, and no `next` at all leaves whatever the gate recorded standing.
  useEffect(() => {
    rememberResumePath(new URLSearchParams(search).get(SIGN_IN_NEXT_PARAM));
  }, [search]);

  // The session became real without a redemption happening here — the boot check finished, or
  // another tab signed this device in while this screen sat open. There is nothing to redeem
  // and nothing to refuse, so the honest thing is to go where they were going.
  useEffect(() => {
    if (fragment !== "absent" || session.status !== "signed-in") return;
    navigate(resumeTarget(session.identity.kind), { replace: true });
  }, [fragment, session, navigate]);

  // Every ARRIVAL of a link (decision #17): the one the page opened with, and one pasted into
  // this tab afterwards. Driven by the `hash` and nothing else, because the hash is the one
  // thing a pasted link is guaranteed to change — the location's `key` is `"default"` on every
  // entry a browser makes for itself (see the head of this file). The strike below empties the
  // hash, so the same link opened again is a change too; the run that strike causes finds no
  // fragment and returns at the first line. This effect is the only thing that acts on `hash`.
  useEffect(() => {
    if (hash === "") return;

    // Strike the fragment from the address bar the moment it has been read, exactly as spec 09
    // §2 requires — before the exchange, not after it, so a failed exchange leaves nothing
    // behind either; and (RUN 87) before the press, so a link that is never pressed leaves
    // nothing in the address bar either. A REPLACE, so the history entry that carried the token
    // stops carrying it rather than gaining a sibling that still does; and made THROUGH THE
    // ROUTER, so the router and the address bar agree that it is gone. The path, the query
    // (`next`) and the gate's landing state are carried across unchanged.
    navigate({ pathname, search }, { replace: true, state: landingState });

    const arrived = readSignInFragment(hash);
    if (arrived.kind === "absent") return;

    // This is now the latest arrival; any exchange still out for an earlier one has lost the
    // right to act, whichever order the answers come back in and whichever screen sent it.
    const arrival = ++latestArrival;
    setFragment(arrived.kind);
    if (arrived.kind === "malformed") {
      setState({ status: "refused", refusal: LINK_UNREADABLE });
      return;
    }

    const token = arrived.token;

    // Move the screen on an exchange's answer for the arrival or press numbered `turn` - but only if
    // nothing newer has happened on this page by the time the answer lands. (A success has already
    // been KEPT by then - stored and signed in - by `sendPressedLink`, whatever this decides.)
    const follow = (turn: number, exchange: Exchange): void => {
      exchange.then(
        (body) => {
          // A newer link has arrived since this one, or a newer press has been made — on this
          // screen or on one mounted since — or a newer link is in the address bar, not yet read.
          // That link keeps the screen: it is held for its own press, and says whose sign-in
          // continuing would replace.
          if (turn !== latestArrival || newerLinkWaitingInAddressBar(token)) return;
          // The destination if one was carried here, and otherwise the landing screen OF THIS
          // KIND OF USER — the Command Desk for the TD, the UM app for an Umpires Manager (gap
          // J9). It is taken, not read: a destination is resumed once and never again.
          navigate(resumeTarget(body.identity.kind), { replace: true });
        },
        (error: unknown) => {
          // Nor may an older link's refusal replace the screen of the exchange that is still out,
          // of the link waiting for its press, or of the link waiting to be read.
          if (turn !== latestArrival || newerLinkWaitingInAddressBar(token)) return;
          // RUN 87B/87C - nobody here knows whether the link was spent (no answer, a time limit run
          // out, a 5xx, or a 200 that could not be used). Say the sign-in could not be confirmed,
          // and stand the button again for the same link.
          if (signInNotConfirmed(error)) {
            setState({ status: "ready", press, waiting: false, unconfirmed: true });
            return;
          }
          const refusal =
            error instanceof ApiError
              ? (REFUSAL[error.code] ?? unexpectedRefusal())
              : unexpectedRefusal();
          setState({ status: "refused", refusal });
        },
      );
    };

    // RUN 87 - HELD, NOT SENT. Arriving is not pressing: the link waits in memory, inside the
    // press below, and the screen stands with `Continue To Sign In`. A newer arrival replaces this
    // view, and the press with it; a reload or a screen that goes away takes it too. Made before
    // the join below (RUN 87B), so that an answer that never arrives - to a press, or to the same
    // link joining one - can stand this button again for the same link.
    const press = (): void => {
      // RUN 87A - one pressed link at a time. The button cannot be pressed while another link's
      // exchange is out; this is the same rule for anything that reaches the press another way.
      if (anotherLinkOut(token)) return;
      // The press is now the newest thing on the page, so it takes the next number: an older
      // press or arrival loses the right to move the screen, and so does this one if a link
      // arrives after it.
      const turn = ++latestArrival;
      setState({ status: "redeeming" });
      // One exchange per token: a second press - or a press of a link whose exchange is out -
      // joins it instead of POSTing a single-use token again.
      let exchange = outstanding.get(token);
      if (exchange === undefined) exchange = sendPressedLink(token, signIn);
      follow(turn, exchange);
    };

    // The same link, arriving while ITS OWN exchange is out. It was pressed on this page already,
    // so it joins that exchange exactly as it always did - nothing is sent - and, being the latest
    // arrival, is the one that acts on the answer (see RUN 87 at the head of this file for why it
    // is not held for a second press).
    const inFlight = outstanding.get(token);
    if (inFlight !== undefined) {
      setState({ status: "redeeming" });
      follow(arrival, inFlight);
      return;
    }

    // RUN 87A - while a different link's exchange is out (pressed a moment ago, on this screen or
    // on one since replaced), this one waits: drawn, but not pressable until that has answered. Its
    // answer, if a success, has been kept by then, so the button comes back saying whose sign-in
    // continuing replaces.
    const others = [...outstanding.values()];
    setState({ status: "ready", press, waiting: others.length > 0, unconfirmed: false });
    if (others.length > 0) {
      void Promise.allSettled(others).then(() => {
        // Only if this link is still the one standing: a newer arrival has its own view.
        setState((current) =>
          current.status === "ready" && current.press === press
            ? { ...current, waiting: false }
            : current,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the hash is the trigger (see above)
  }, [hash]);

  // Somebody typed the path, a mail client dropped the fragment, or the session gate sent a
  // signed-out device here. There is no link to redeem, so the honest screen is the
  // signed-out one — saying which of the three signed-out situations this is, when the gate
  // said. A fragment that arrived and could not be read is a different case, and falls
  // through to the refusal below.
  if (fragment === "absent") {
    const landing = landingState as SignInLandingState | null;
    return <SignedOutPage reason={landing?.reason ?? "no_credential"} />;
  }

  // What the device is already signed in as, known without asking the network (L1b, RUN 87A).
  const already = signedInAs(session);

  // RUN 87 - a link has arrived and waits for the one press that sends it. The hint above the
  // button is advisory (`app/inAppBrowser.ts`): the button stands in every browser.
  if (state.status === "ready") {
    const press = state.press;
    const inApp = inAppBrowserName();
    return (
      <div className="signedout">
        <section className="panel panel--d1 signedout__panel" aria-labelledby="readyTitle">
          <div className="panel__head">
            <h3 className="panel__title" id="readyTitle">
              Sign In On This Device
            </h3>
            <span className="panel__note">TD Control Centre And UM Feedback App</span>
          </div>
          <div className="signin__ready">
            {state.unconfirmed ? (
              <p className="signin__unconfirmed" role="alert">
                <b>Sign-In Not Confirmed</b>
                This device could not confirm the sign-in. Press Continue To Sign In again. If it
                then says the link has already been used, reload this page: this device may already
                be signed in. If it is not, ask the Technical Delegate for a new link.
              </p>
            ) : null}
            {inApp === null ? null : (
              <p className="signin__inapp" role="note">
                <b>Opened Inside {inApp}</b>
                For the best result, open this link in Safari or Chrome instead: go back to your
                message and open it from there. Signing in here signs in only the browser inside{" "}
                {inApp}. You can still continue here.
              </p>
            )}
            {already === null ? null : (
              <p className="signin__already" role="note">
                <b>Already Signed In</b>
                {already.name === null
                  ? "This device is already signed in."
                  : `This device is already signed in as ${already.name}.`}{" "}
                Continuing replaces that sign-in with the one from this link.
              </p>
            )}
            <button
              type="button"
              className="gbtn gbtn--solid signin__continue"
              aria-describedby="continueWhat"
              disabled={press === null || state.waiting}
              onClick={(event) => {
                // L1a (RUN 87A) - the second half of a double-click is not a press. A page that
                // gets a double-click started can put this button under its second click (the
                // DoubleClickjacking shape). A keyboard press reports 0, a tap or click 1.
                if (event.detail > 1) return;
                press?.();
              }}
            >
              Continue To Sign In
            </button>
            {state.waiting ? (
              <p className="signin__wait" role="status">
                The link you pressed a moment ago is still signing this device in. You can
                continue with this one once it has answered. If it has not finished within a
                minute, reload this page and open the newest link from your message again.
              </p>
            ) : null}
            <p className="signin__what" id="continueWhat">
              Pressing it pairs this device with your personal link and signs you in. Opening the
              link did not use it up.
            </p>
          </div>
          <div className="panel__foot">
            <b>Not the device you will use?</b> Stop here, and open the link from your message on
            the one you will use during the tournament. A link works once, and only this button
            uses it. You can also ask the Technical Delegate for a new link, which cancels this
            one.
          </div>
        </section>
      </div>
    );
  }

  if (state.status === "redeeming") {
    return (
      <div className="signedout">
        <section className="panel panel--d1 signedout__panel" aria-labelledby="redeemingTitle">
          <div className="panel__head">
            <h3 className="panel__title" id="redeemingTitle">
              Signing You In
            </h3>
            {/* No event name here, deliberately. This screen runs BEFORE the credential
                exists, so it has no authenticated read to learn the tournament from, and the
                fixed `Asian Games 2026` string it used to carry was wrong on any other
                installation. The chrome names the tournament from `GET /auth/me` a moment
                later; this note names the application only. */}
            <span className="panel__note">TD Control Centre And UM Feedback App</span>
          </div>
          <Skeleton rows={3} />
          <div className="panel__foot">
            This device is being paired to your link. It happens once — after this the app
            opens signed in.
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="signedout">
      <section className="panel panel--d1 signedout__panel" aria-labelledby="refusedTitle">
        <div className="panel__head">
          <h3 className="panel__title" id="refusedTitle">
            {state.refusal.title}
          </h3>
          <span className="panel__note">Sign-In Link</span>
        </div>
        <div className="emptystate" role="alert">
          <b>{state.refusal.detail}</b>
          {/* RUN 87A - true whichever way the device stands. It can be signed in while a refusal
              shows: it was signed in before this link, or a link pressed before this one has since
              signed it in (a pressed link's success is kept - see the head of this file). */}
          {already === null
            ? "Nothing has been signed in on this device."
            : already.name === null
              ? "This device is signed in, and this link changed nothing."
              : `This device is signed in as ${already.name}, and this link changed nothing.`}
        </div>
        {/* RUN 87B - a way on for a device that IS signed in (it was before this link, or a link
            pressed before this one has signed it in since): into the app, to the same place a
            sign-in made here would have gone - a destination carried here, or the landing screen
            of this kind of user. Only once the session knows who that is; a credential still being
            checked, or one the API could not be reached to check, has no kind to land on yet. */}
        {session.status === "signed-in" ? (
          <div className="signin__onward">
            <button
              type="button"
              className="gbtn gbtn--solid signin__continue"
              onClick={() => {
                navigate(resumeTarget(session.identity.kind), { replace: true });
              }}
            >
              Continue As {session.identity.appShortName}
            </button>
          </div>
        ) : null}
        <div className="panel__foot">
          <b>There is no self-service re-send.</b> This application never sends email — the
          Control Centre produces the link and the Technical Delegate passes it to you
          directly. There is no password to fall back on, and nothing to sign up for.
        </div>
      </section>
    </div>
  );
}
